<template>
    <div
        ref="emailInput"
        class="email-input"
        :class="{ 'is-active': isFilled || (isFilled && !$parent.allInputsBlurred) }"
    >
        <label :for="inputId" class="typographies-paragraph-medium-regular" :class="labelClass">
            {{ label }}
        </label>
        <div class="email-input__input" @click="onFocus" @focusout="onBlur" @keydown.esc="onBlur">
            <slot name="tags" />
            <input
                v-if="!shouldHideInput"
                :id="inputId"
                :ref="inputId"
                v-model="input"
                :placeholder="
                    isFocused && !isFilled
                        ? $t.__('email.emailPanels.EmailInput.enter_recipient')
                        : ''
                "
                class="typographies-paragraph-medium-regular"
                @input="onInput"
                @focus="onFocus"
                @keydown.,.prevent="onValidateEmail"
                @keydown.tab.prevent="onValidateEmail"
                @keydown.enter.prevent="onValidateEmail"
                @keydown.down="onNavigation('down')"
                @keydown.up="onNavigation('up')"
            />
        </div>
        <div class="actions" :class="{ 'all-line': emailAddresses.length > 0 && isFocused }">
            <slot name="actions"></slot>
        </div>
        <EmailInputDropdown
            v-show="dataFromAutocomplete.length > 0 && isFocused"
            :id="`inputDropdown${hashPanel}`"
            :ref="`inputDropdown${hashPanel}`"
            dropdownClass="email-input__dropdown"
            :parentRef="inputId"
            :minWidth="0"
            :isVisible="dataFromAutocomplete.length > 0 && isFocused"
            :loading="isLoading"
        >
            <div>
                <CompassDropdownOption
                    v-for="(item, index) in dataFromAutocomplete"
                    :key="index"
                    :label="`${item.name} (${item.email})`"
                    :value="item.id"
                    :hovered="itemHovered === index"
                    class="dropdown-email__option"
                    @pointerdown.prevent="onItemSelected(item.id)"
                    @mouseenter="onItemHovered(index)"
                >
                    <CompassAvatar
                        :initials="`${item.firstName.charAt(0)}${item.lastName.charAt(0)}`"
                        :photo="getPhotoAvatar(item.avatar)"
                        :colorType="getAvatarRandomColor(item)"
                        size="extra-small"
                    />
                    <div class="dropdown-email__avatar-text">
                        <span
                            class="avatar-name"
                            :class="{
                                'email-already-added': checkIfEmailAlreadyAdded(item.email),
                            }"
                        >
                            {{ item.firstName }} {{ item.lastName }}
                        </span>
                        <span class="avatar-email typographies-paragraph-small-regular">
                            {{ item.email }}
                        </span>
                    </div>
                    <CompassIcon
                        v-if="checkIfEmailAlreadyAdded(item.email)"
                        icon="check"
                        type="link"
                        class="dropdown-email__check-icon"
                    />
                </CompassDropdownOption>
            </div>
        </EmailInputDropdown>
    </div>
</template>
<script>
import generateHash from 'Helpers/hashGenerator.js';
import EmailInputDropdown from './EmailInputDropdown.vue';
import { randAvatarColorFullname } from 'utils/avatar';
import { createNamespacedHelpers } from 'vuex';
const { mapState: mapStateUser } = createNamespacedHelpers('user');
import { validateEmail } from 'Helpers/validator/EmailValidator';
import { searchRecipients } from 'api/emailEngine/emailEngineService';
import { debounce } from 'lodash';

export default {
    components: { EmailInputDropdown },
    props: {
        label: {
            type: String,
            required: true,
        },
        type: {
            type: String,
            required: true,
        },
        emailAddresses: {
            type: Array,
            required: true,
        },
        isFocused: {
            type: Boolean,
            required: false,
            default: false,
        },
        shouldHideInput: {
            type: Boolean,
            required: false,
            default: false,
        },
        hashPanel: {
            type: String,
            required: false,
            default: '',
        },
    },
    data() {
        return {
            input: '',
            inputId: '',
            labelClass: [],
            dataFromAutocomplete: [],
            hash: '',
            dropdownWidth: 0,
            itemHovered: 0,
            needToUpdateAndOpenDropdown: false,
            separatorRegex: /[,;]/,
            isLoading: false,
        };
    },
    computed: {
        ...mapStateUser({
            recomendedUsers: 'recomendedUsers',
        }),
        isFilled() {
            return !!(this.emailAddresses.length || this.input);
        },
    },
    watch: {
        isFocused: function (newFocus) {
            if (newFocus) {
                this.focusInput();
                this.checkHeightInput();
                this.dataFromAutocomplete = this.filterRecommendedUsers();
            } else {
                this.onValidateEmail();

                if (this.needToUpdateAndOpenDropdown) {
                    this.needToUpdateAndOpenDropdown = false;
                    this.onFocus();
                    this.checkHeightInput();
                    this.dataFromAutocomplete = this.filterRecommendedUsers();
                }
            }
        },
        'emailAddresses.length': function (newLength) {
            this.$nextTick(() => {
                if (newLength > 0) {
                    this.checkHeightInput();
                }
            });
        },
    },
    mounted() {
        if (this.isFocused) {
            this.focusInput();
        }
    },
    created() {
        this.hash = generateHash();
        this.inputId = `email-input-${this.type}-${this.hash}`;
    },
    methods: {
        checkIfEmailAlreadyAdded(email) {
            return this.emailAddresses.some((item) => item.email === email);
        },
        filterRecommendedUsers() {
            if (!this.emailAddresses || !this.recomendedUsers) {
                return [];
            }

            return this.recomendedUsers.filter((user) => {
                return !this.emailAddresses.some(
                    (emailAddress) => emailAddress.email === user.email,
                );
            });
        },
        onNavigation(direction) {
            {
                if (!this.dataFromAutocomplete.length) {
                    return;
                }
                if (direction === 'down') {
                    this.itemHovered =
                        this.itemHovered === this.dataFromAutocomplete.length - 1
                            ? 0
                            : ++this.itemHovered;
                } else {
                    this.itemHovered =
                        this.itemHovered === 0
                            ? (this.itemHovered = this.dataFromAutocomplete.length - 1)
                            : --this.itemHovered;
                }
            }
        },
        onItemHovered(index) {
            this.itemHovered = index;
        },
        focusInput() {
            document.getElementById(this.inputId)?.focus();
        },
        blurInput() {
            document.getElementById(this.inputId)?.blur();
        },
        onItemSelected(value) {
            const contact = this.dataFromAutocomplete.find((item) => item.id === value);
            this.addEmailAddress(contact);
        },
        onInput: debounce(function () {
            if (this.input === '') {
                this.dataFromAutocomplete = this.filterRecommendedUsers();
                return;
            }

            this.isLoading = true;
            searchRecipients(this.input)
                .then((response) => {
                    this.dataFromAutocomplete = response.data.map((item) => ({
                        email: item.email,
                        firstName: item.first_name,
                        lastName: item.last_name,
                        id: item.object.id,
                        type: item.object.type,
                        avatar: item.avatar ?? null,
                    }));

                    this.dataFromAutocomplete = this.dataFromAutocomplete.filter((item) =>
                        this.emailAddresses.every((email) => email.email !== item.email),
                    );
                })
                .catch(() => {
                    this.$pushToast({
                        type: 'error',
                        close: true,
                        text: this.$t.__('email.emailPanels.EmailInput.search_recipients_error'),
                    });
                })
                .finally(() => {
                    this.isLoading = false;
                    if (this.dataFromAutocomplete.length > 0) {
                        requestAnimationFrame(() => {
                            const dropdownElement = this.$refs[`inputDropdown${this.hashPanel}`];
                            const widthSellsyApp =
                                document.getElementById('sellsy_app').offsetWidth;
                            const dropdownLeftPosition =
                                dropdownElement.$el.getBoundingClientRect().left;
                            this.dropdownWidth = widthSellsyApp - (dropdownLeftPosition + 48);
                        });
                    }
                });
        }, 400),
        onFocus() {
            this.focusInput();
            this.$emit('focus');
        },
        hasWhiteSpace(s) {
            return /\s/g.test(s);
        },
        onBlur() {
            this.dataFromAutocomplete = [];
            this.onValidateEmail();
            const isFilled = !!this.emailAddresses.length || !!this.input;
            this.$emit('blur', isFilled);
        },
        isValidEmail(email) {
            return validateEmail(email) && !this.checkIfEmailAlreadyAdded(email);
        },
        onValidateEmail() {
            const emailInput = this.input.trim();
            if (
                this.dataFromAutocomplete.length > 0 &&
                !!this.itemHovered &&
                !!this.dataFromAutocomplete[this.itemHovered]
            ) {
                this.onItemSelected(this.dataFromAutocomplete[this.itemHovered].id);
            } else if (this.hasEmailSeparator(emailInput)) {
                for (const email of this.getEmailsArray(emailInput)) {
                    this.addNotRegisteredEmail(email);
                }
            } else if (emailInput.length && !this.hasWhiteSpace(emailInput)) {
                this.addNotRegisteredEmail(emailInput);
            }
        },
        hasEmailSeparator(emailInput) {
            return this.separatorRegex.test(emailInput);
        },
        getEmailsArray(emailInput) {
            return new Set(
                emailInput
                    .split(this.separatorRegex)
                    .map((email) => email.trim())
                    .filter((email) => email.length),
            );
        },
        addNotRegisteredEmail(email) {
            const recipient = {
                email: email,
                isValid: this.isValidEmail(email),
                type: this.type,
                id: generateHash(),
            };

            this.$emit('onAddTag', recipient);
            this.input = '';
        },
        addEmailAddress(contact = null) {
            if (
                this.emailAddresses.some((email) => email.address === contact?.email ?? this.input)
            ) {
                return;
            }
            const recipient = {
                email: contact?.email ?? this.input,
                isValid: validateEmail(contact?.email ?? this.input),
                type: this.type,
                id: generateHash(),
            };
            if (contact) {
                recipient.name = `${contact?.firstName} ${contact?.lastName}`;
                recipient.related = [
                    {
                        id: contact?.id,
                        type: contact?.type,
                    },
                ];
            }
            this.dataFromAutocomplete = [];
            this.$emit('onAddTag', recipient);
            window.EventBus.$emit('onAddTag', recipient);
            this.input = '';

            if (this.isFocused) {
                // blur the preview dropdown
                this.blurInput();
                this.needToUpdateAndOpenDropdown = true;
            }
        },
        checkHeightInput() {
            this.$nextTick(() => {
                const emailInput = this.$refs.emailInput;
                this.labelClass = emailInput?.clientHeight > 32 ? ['alignStart'] : [''];
            });
        },
        getFullName(item) {
            return `${item.firstName} ${item.lastName}`;
        },
        getAvatarRandomColor(item) {
            return randAvatarColorFullname(this.getFullName(item));
        },
        getPhotoAvatar(avatar) {
            return avatar?.type === 'picture' ? avatar.value : '';
        },
    },
};
</script>

<style lang="scss" scoped>
.email-input {
    align-items: center;
    border-bottom: 1px solid var(--colors-common-border-lighter);
    display: flex;
    gap: var(--spacing-1);
    padding: var(--spacing-0) 0;
    line-height: var(--line-heights-24);

    &:has(input:focus) {
        border-color: var(--colors-common-border-focus);
        label {
            color: var(--colors-common-text-standard-default);
            font-weight: var(--font-weights-proxima-nova-0);
        }
    }

    &.is-active {
        label {
            display: flex;
            align-items: center;
            height: 24px;
            color: var(--colors-common-text-standard-default);
            font-weight: var(--font-weights-proxima-nova-0);
        }
    }

    label {
        display: flex;
        align-items: center;
        height: 24px;
        user-select: none;
        color: var(--colors-common-text-standard-placeholder);
        &.alignStart {
            align-self: start;
        }
    }

    input {
        border: none !important;
        flex-grow: 1;
        margin-left: var(--spacing-1);
        padding: 0 !important;
        text-overflow: ellipsis;
    }

    &__input {
        display: flex;
        flex-direction: row;
        flex-grow: 1;
        flex-wrap: wrap;
        gap: var(--spacing-0);
    }

    .actions {
        display: flex;
        align-items: center;
        flex-shrink: 0;
        align-self: start;
        margin-left: auto;
        height: 24px;
    }

    &__dropdown {
        z-index: 1001;
        max-width: 312px;
        .compass-dropdown-option {
            padding-top: var(--spacing-1);
            padding-bottom: var(--spacing-1);
        }

        .dropdown-email__avatar-text {
            display: flex;
            flex-direction: column;
            .avatar-name {
                line-height: 17px;
                &.email-already-added {
                    font-weight: 600;
                }
            }
            .avatar-email {
                color: var(--colors-common-text-standard-caption);
            }
        }

        .dropdown-email__option {
            position: relative;
        }

        .dropdown-email__check-icon {
            position: absolute;
            right: var(--spacing-2);
            top: 50%;
            transform: translateY(-50%);
        }
    }
}
</style>
